import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaExpand,
  FaChartBar,
  FaMapMarkerAlt,
  FaExclamationCircle,
  FaSignOutAlt,
  FaPlus,
  FaTimes,
} from "react-icons/fa";

const CITRIOT_LOGO = "/citriot_logo.webp";
const EMPIRE_LOGO = "/empirelogo.webp";

const focusRing =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2";

const demoFeeds = [
  {
    id: 1,
    name: "Dining Area",
    location: "Dining Area",
    image:
      "https://frontend-citriot.s3.us-east-2.amazonaws.com/Empire/Kammanahalli_Frames/192.168.39.75/channel6.jpg",
  },
  {
    id: 2,
    name: "Store Entrance",
    location: "Store Entry / Exit",
    image:
      "https://frontend-citriot.s3.us-east-2.amazonaws.com/Empire/Kammanahalli_Frames/192.168.39.75/channel16.jpg",
  },
  {
    id: 3,
    name: "Cash Counter",
    location: "Cash Counter",
    image:
      "https://frontend-citriot.s3.us-east-2.amazonaws.com/Empire/Kammanahalli_Frames/192.168.39.15/channel4.jpg",
  },
  {
    id: 4,
    name: "Kitchen",
    location: "Kitchen / Frying Area",
    image:
      "https://frontend-citriot.s3.us-east-2.amazonaws.com/Empire/Kammanahalli_Frames/192.168.39.75/channel11.jpg",
  },
];

const STATUS_LABEL = {
  loaded: "Online",
  error: "Unavailable",
  loading: "Connecting",
};

const STATUS_DOT = {
  loaded: "bg-emerald-500",
  error: "bg-red-500",
  loading: "bg-amber-400",
};

const EmpireLiveView = () => {
  const navigate = useNavigate();

  const [camera, setCamera] = useState(null);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [expandedFeed, setExpandedFeed] = useState(null);

  const [imageStatus, setImageStatus] = useState(() =>
    demoFeeds.reduce((acc, feed) => {
      acc[feed.id] = "loading";
      return acc;
    }, {})
  );

  useEffect(() => {
    const savedCamera = sessionStorage.getItem("empireCamera");

    if (savedCamera) {
      try {
        const parsedCamera = JSON.parse(savedCamera);
        setCamera(parsedCamera);
        setSelectedCamera({
          ...demoFeeds[0],
          name: parsedCamera.cameraName || demoFeeds[0].name,
          location: parsedCamera.location || demoFeeds[0].location,
        });
      } catch (error) {
        console.error("Failed to read saved camera:", error);
        setSelectedCamera(demoFeeds[0]);
      }
    } else {
      setSelectedCamera(demoFeeds[0]);
    }
  }, []);

  const onlineCount = Object.values(imageStatus).filter(
    (s) => s === "loaded"
  ).length;

  const setStatus = (id, status) =>
    setImageStatus((prev) => ({ ...prev, [id]: status }));

  const handleExpand = (feed) => {
    if (imageStatus[feed.id] !== "loaded") return;
    setExpandedFeed(feed);
  };

  const closeExpanded = () => setExpandedFeed(null);

  // Close on Escape and lock page scroll while the modal is open
  useEffect(() => {
    if (!expandedFeed) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setExpandedFeed(null);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [expandedFeed]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* ================= HEADER ================= */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1500px] items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-4">
            <img
              src={CITRIOT_LOGO}
              alt="Citriot"
              className="h-7 w-auto object-contain"
            />
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
                <img
                  src={EMPIRE_LOGO}
                  alt="Empire"
                  className="h-full w-full object-contain p-1.5"
                />
              </div>
              <div className="hidden leading-tight sm:block">
                <p className="text-sm font-bold text-slate-900">Empire</p>
                <p className="text-[11px] text-slate-400">
                  Camera Intelligence
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 md:flex">
              <span className="relative flex h-2 w-2">
                <span className="absolute h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none" />
                <span className="relative h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-xs font-medium text-emerald-700">
                System online
              </span>
            </div>

            <div className="flex items-center gap-3 border-l border-slate-200 pl-3">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                  A
                </div>
                <div className="hidden leading-tight md:block">
                  <p className="text-sm font-semibold text-slate-900">Admin</p>
                  <p className="text-[11px] text-slate-400">Administrator</p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                className={`flex h-9 items-center gap-2 rounded-lg px-3 text-xs font-semibold text-slate-600 transition hover:bg-red-50 hover:text-red-600 ${focusRing}`}
              >
                <FaSignOutAlt size={13} />
                <span className="hidden sm:inline">Log out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ================= MAIN ================= */}
      <main className="mx-auto max-w-[1500px] px-5 py-8 sm:px-8 lg:py-12">
        {/* TITLE + ACTIONS */}
        <div className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div>
            <p className="text-sm text-slate-400">
              Live monitoring <span className="mx-1">/</span> Hikvision
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              Camera monitoring
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              <span className="font-semibold text-slate-700">
                {onlineCount} of {demoFeeds.length}
              </span>{" "}
              cameras online. Showing the latest available frame from each.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate("/empire/add-camera")}
              className={`flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 ${focusRing}`}
            >
              <FaPlus className="text-xs" />
              Add camera
            </button>

            <button
              type="button"
              onClick={() => navigate("/empire/build-dashboard")}
              className={`flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 ${focusRing}`}
            >
              <FaChartBar className="text-xs" />
              Open dashboard
            </button>
          </div>
        </div>

        {/* CONNECTED CAMERA */}
        {camera && (
          <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-bold text-slate-950">
                    {camera.cameraName}
                  </h2>
                  <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Connected
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  {camera.location}
                </p>
              </div>

              <dl className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
                <Detail label="IP address" value={camera.ipAddress} />
                <Detail label="Port" value={camera.port} />
                <Detail label="Channel" value={camera.channel} />
                <Detail label="Protocol" value={camera.protocol || "RTSP"} />
              </dl>
            </div>
          </section>
        )}

        {/* CAMERA FEEDS */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {demoFeeds.map((feed) => {
            const isSelected = selectedCamera?.id === feed.id;
            const status = imageStatus[feed.id];

            return (
              <article
                key={feed.id}
                onClick={() => setSelectedCamera(feed)}
                className={`cursor-pointer overflow-hidden rounded-2xl bg-white transition ${
                  isSelected
                    ? "border border-blue-400 ring-4 ring-blue-50"
                    : "border border-slate-200 hover:border-slate-300 hover:shadow-md"
                }`}
              >
                {/* IMAGE */}
                <div className="relative aspect-video overflow-hidden bg-slate-100">
                  {status === "loading" && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-100">
                      <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600 motion-reduce:animate-none" />
                      <p className="mt-3 text-sm font-medium text-slate-600">
                        Connecting to camera
                      </p>
                    </div>
                  )}

                  {status === "error" && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-50 px-6 text-center">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-50">
                        <FaExclamationCircle className="text-red-500" />
                      </div>
                      <p className="mt-3 text-sm font-semibold text-slate-700">
                        Can't load this feed
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        Check the camera's network connection and refresh.
                      </p>
                    </div>
                  )}

                  <img
                    src={feed.image}
                    alt={feed.name}
                    onLoad={() => setStatus(feed.id, "loaded")}
                    onError={() => setStatus(feed.id, "error")}
                    className={`h-full w-full object-cover transition-opacity duration-300 ${
                      status === "loaded" ? "opacity-100" : "opacity-0"
                    }`}
                    loading="eager"
                    decoding="async"
                  />

                  {status === "loaded" && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" />

                      <div className="absolute left-4 top-4 flex items-center gap-2 rounded-md bg-red-600 px-2.5 py-1 shadow-sm">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white motion-reduce:animate-none" />
                        <span className="text-xs font-semibold text-white">
                          Live
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExpand(feed);
                        }}
                        className={`absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-lg bg-white/90 text-slate-700 shadow-sm backdrop-blur-md transition hover:bg-white hover:text-slate-950 ${focusRing}`}
                        aria-label={`Expand ${feed.name}`}
                      >
                        <FaExpand className="text-xs" />
                      </button>

                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-lg font-semibold text-white">
                          {feed.name}
                        </h3>
                        <p className="mt-0.5 flex items-center gap-1.5 text-sm text-white/80">
                          <FaMapMarkerAlt className="text-xs" />
                          {feed.location}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* FOOTER */}
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 rounded-full ${STATUS_DOT[status]}`}
                    />
                    <span className="text-sm text-slate-600">
                      {STATUS_LABEL[status]}
                    </span>
                  </div>

                  <span className="font-mono text-xs text-slate-400">
                    CAM-{String(feed.id).padStart(2, "0")}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </main>

      {/* ================= EXPAND MODAL ================= */}
      {expandedFeed && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm sm:p-8"
          onClick={closeExpanded}
          role="dialog"
          aria-modal="true"
          aria-label={`${expandedFeed.name} camera feed`}
        >
          <div
            className="relative w-full max-w-6xl overflow-hidden rounded-2xl bg-slate-900 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 border-b border-white/10 px-5 py-3.5">
              <div className="min-w-0">
                <h3 className="truncate text-base font-semibold text-white">
                  {expandedFeed.name}
                </h3>
                <p className="mt-0.5 flex items-center gap-1.5 text-sm text-white/60">
                  <FaMapMarkerAlt className="text-xs" />
                  {expandedFeed.location}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 rounded-md bg-red-600 px-2.5 py-1">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white motion-reduce:animate-none" />
                  <span className="text-xs font-semibold text-white">Live</span>
                </div>

                <button
                  type="button"
                  onClick={closeExpanded}
                  autoFocus
                  aria-label="Close"
                  className={`flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white transition hover:bg-white/20 ${focusRing}`}
                >
                  <FaTimes className="text-sm" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-center bg-black">
              <img
                src={expandedFeed.image}
                alt={expandedFeed.name}
                className="max-h-[78vh] w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="text-xs text-slate-400">{label}</dt>
    <dd className="mt-0.5 truncate font-mono text-sm font-medium text-slate-800">
      {value || "-"}
    </dd>
  </div>
);

export default EmpireLiveView;